import { useEffect, useRef, useState } from 'react';
import {
  findDeletedSectionsWithItems,
  isStagingDirty,
  toBatchPayload,
  useCatalogSectionStaging,
} from '../../hooks/useCatalogSectionStaging';
import { UseCatalogSectionsDropdownParams } from './catalogSectionsDropdown.types';

export const useCatalogSectionsDropdown = ({
  sections,
  applyMutation,
  onClose,
}: UseCatalogSectionsDropdownParams) => {
  const staging = useCatalogSectionStaging({ persisted: sections });
  const [draftName, setDraftName] = useState('');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [scrollToKey, setScrollToKey] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToKey === null) {
      return;
    }

    const row = listRef.current?.querySelector(`[data-row-key="${scrollToKey}"]`);
    row?.scrollIntoView({ block: 'nearest' });
    setScrollToKey(null);
  }, [scrollToKey]);

  const deletedWithItems = findDeletedSectionsWithItems(staging.entries, sections);

  const handleCreate = () => {
    const name = draftName.trim();

    if (name.length === 0) {
      return;
    }

    setScrollToKey(staging.create(name));
    setDraftName('');
  };

  const handleCommitEdit = (name: string) => {
    const trimmed = name.trim();

    if (trimmed.length > 0 && staging.editingKey !== null) {
      staging.rename({ key: staging.editingKey, name: trimmed });
    }

    staging.cancelEdit();
  };

  const handleApply = () => {
    if (deletedWithItems.length > 0 && !isConfirmingDelete) {
      setIsConfirmingDelete(true);
      return;
    }

    setIsConfirmingDelete(false);
    applyMutation.save({ sections: toBatchPayload(staging.entries) }, { onSuccess: onClose });
  };

  return {
    entries: staging.entries,
    editingKey: staging.editingKey,
    listRef,
    draftName,
    setDraftName,
    isApplyEnabled: isStagingDirty(staging.entries, sections) && !applyMutation.isSaving,
    deletedWithItems,
    isConfirmingDelete,
    cancelDeleteConfirm: () => setIsConfirmingDelete(false),
    reorder: staging.reorder,
    remove: staging.remove,
    startEdit: staging.startEdit,
    cancelEdit: staging.cancelEdit,
    handleCreate,
    handleCommitEdit,
    handleApply,
  };
};
